import { useQuery } from '@tanstack/react-query';

import type { ApiSuccessResponse } from '@nf/types/api';
import { SwitchFromEnum, type SiteConfigsSettingsData, type SiteConfigsSettingsWithSessionData } from '@nf/types/site';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import queryKeys from '@/constants/query-keys';
import { useSiteStore } from '@/store/site';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { getAuthToken } from '@/store/auth';
import { useAtomValue } from 'jotai';
import { visitorAtom } from '@/atoms/visitor';
import { Breakpoint } from '@/features/breakpoint';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { siteService } from '@/services/site.service';
import { LicenseeTypeEnums } from '@nf/types/site';

export const getSiteConfigs = async (switchFrom: number): Promise<
	ApiSuccessResponse<SiteConfigsSettingsData>
> => {
	const url = cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/api/Config/v2/GetSiteConfigs`) + `?switchFrom=${switchFrom}`;
	const response = await fetchComposer.get(url);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
}

export const useGetSiteConfigs = () => {
	const { breakpoint } = useBreakpoint();
	const isAthena = siteService.getLicenseeType() === LicenseeTypeEnums.Athena;
	const switchFrom = useSiteStore.getState().deepLinkSiteInfo.switchFrom === SwitchFromEnum.Default && isAthena ? 
		(Breakpoint.md > breakpoint ? SwitchFromEnum.Mobile : SwitchFromEnum.Desktop) 
		: 
		(useSiteStore.getState().deepLinkSiteInfo.switchFrom === SwitchFromEnum.Default ?
			SwitchFromEnum.Mobile : useSiteStore.getState().deepLinkSiteInfo.switchFrom);

	const mockSiteId = useSiteStore(state => state.mockSiteId);

	const { data, isLoading, isError, error, refetch, isFetched } = useQuery({
		queryKey: [queryKeys.SITE_CONFIGS],
		queryFn: () => getSiteConfigs(switchFrom),
		staleTime: 1000 * 30,
		cacheTime: 1000 * 60,
		refetchOnWindowFocus: false,
		enabled: (useSiteStore.getState().apiDomain.ctcdDomain ?? '') != '',
	});

	const siteConfigsData = data?.Data && mockSiteId
		? { ...data?.Data, GetMessageID: mockSiteId }
		: data?.Data;

	return {
		SiteConfigsData: siteConfigsData,
		isLoading,
		isError,
		error,
		refetchSiteConfigs: refetch,
		isFetched
	};
};

export const getSiteConfigsWithSession = async (accessToken: string | undefined): Promise<
	ApiSuccessResponse<SiteConfigsSettingsWithSessionData>
> => {
	const url = cloudUrlParser(`${useSiteStore.getState().apiDomain.ctcdDomain}/api/Config/GetSiteConfigWithSession`);
	const response = await fetchComposer.getWithBearer(url)((accessToken));

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
}

export const useGetSiteConfigsWithSession = () => {
	const authToken = getAuthToken();
	const isBefore = !authToken;
	const visitorInfo = useAtomValue(visitorAtom);
	const useToken = isBefore ? visitorInfo?.AccessToken : authToken;

	// 加上isBefore key, 若登入需要重新fetch資訊
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.SITE_CONFIGS_WITH_SESSION, isBefore],
		queryFn: () => getSiteConfigsWithSession(useToken),
		staleTime: 1000 * 30,
		cacheTime: 1000 * 60,
		refetchOnWindowFocus: false,
		enabled: !!useToken,
	});

	return {
		SiteConfigsWithSessionData: data?.Data,
		isLoading,
		isError,
		error,
		refetchSiteConfigs: refetch,
	};
};